import { useState } from 'react'
import { Button } from '@cargomart/ui'
import { Outlet, useNavigate } from '@tanstack/react-router'
import cn from 'classnames'
import { Seo } from 'components'
import ModalItem from 'components/ModalItem'
import { MODAL_IDS, useModal } from 'components/ModalProvider'
import { ModalSimple } from 'components/ModalSimple'
import { useOnMount } from 'hooks'
import { useDeleteCustomerMutation, useGetCustomerListQuery } from 'modules/customer/api'
import { Table } from 'modules/customer/components/Table/Table'
import { TCustomer } from 'modules/customer/types'

import { customerCreateRoute, customerDetailRoute } from '../../routes'

export const CustomerList = () => {
  const navigate = useNavigate()
  const { openModal, closeModal } = useModal()
  const { data: customers, isFetching: isCustomerListFetching, refetch } = useGetCustomerListQuery()
  const [deleteCustomer, { isLoading: isDeleting }] = useDeleteCustomerMutation()
  const [focusedCustomer, setFocusedCustomer] = useState<TCustomer | null>(null)
  const title = 'контрагента'
  const subtitle = 'контрагентом'

  const handleOpenModalDelete = (e: React.SyntheticEvent, customer: TCustomer) => {
    e.stopPropagation()
    setFocusedCustomer(customer)

    openModal(MODAL_IDS.DeleteCustomerList)
  }

  const handleOpenModalDetail = (customer: TCustomer) => {
    setFocusedCustomer(customer)
    navigate({ to: customerDetailRoute.to, params: { id: customer.id } })
  }

  const handleOpenCustomerAdd = () => {
    navigate(customerCreateRoute)
  }

  const handleDelete = () => {
    if (focusedCustomer) {
      deleteCustomer(focusedCustomer.id).finally(() => {
        closeModal(MODAL_IDS.DeleteCustomerList)
        refetch()
      })
    }
  }

  useOnMount(() => {
    refetch()
  })

  return (
    <>
      <Seo
        description="Справочник контрагентов, добавленных пользователем в сервис грузоперевозок QP5 Express."
        title="Контрагенты грузоперевозок – сборные перевозки QP5 Express"
      />
      <ModalItem id={MODAL_IDS.DeleteCustomerList}>
        <ModalSimple
          id={MODAL_IDS.DeleteCustomerList}
          onClose={() => closeModal(MODAL_IDS.DeleteCustomerList)}
          title={`Удалить ${title} «${focusedCustomer ? focusedCustomer.name : '-'}»?`}
          mobileModalFooter={
            <Button
              className="block w-full"
              disabled={!focusedCustomer?.access.delete}
              loading={isDeleting}
              onClick={handleDelete}
              size="lg"
              variant="solid"
            >
              Удалить
            </Button>
          }
          modalFooter={
            <div className="ml-auto flex gap-x-2">
              <Button
                className="ml-auto"
                onClick={() => closeModal(MODAL_IDS.DeleteCustomerList)}
                size="lg"
                variant="outline"
              >
                Не удалять
              </Button>
              <Button
                disabled={!focusedCustomer?.access.delete}
                loading={isDeleting}
                onClick={handleDelete}
                size="lg"
                variant="solid"
              >
                Удалить
              </Button>
            </div>
          }
        >
          {`В заявках, которые были ранее созданы с этим ${subtitle}, информация о нём сохранится.`}
        </ModalSimple>
      </ModalItem>
      <Outlet />
      <div
        className={cn(
          'relative mx-auto my-0 flex w-full max-w-[1400px] items-center justify-center px-4 py-8',
          'max-2lg:flex max-2lg:flex-col',
          'max-2md:w-full max-2md:px-0 max-2md:pb-12 max-2md:pt-10'
        )}
      >
        <Table
          customers={customers}
          deleteCustomer={deleteCustomer}
          handleOpenModalDelete={handleOpenModalDelete}
          handleOpenModalDetail={handleOpenModalDetail}
          isLoading={isCustomerListFetching}
          onAddCustomer={handleOpenCustomerAdd}
          refetch={refetch}
        />
      </div>
    </>
  )
}
